import { useEffect, RefObject } from 'react';
import { useGlobalKeyboard, KEYBOARD_PRIORITIES } from './useGlobalKeyboard';

export interface ModalOptions {
  isOpen: boolean;
  onClose: () => void;
  containerRef?: RefObject<HTMLElement>;
  triggerRef?: RefObject<HTMLElement>;
  initialFocusRef?: RefObject<HTMLElement>;
  lockBodyScroll?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

/**
 * モーダル・オーバーレイ・フォーカス管理を統一するフック
 * Sidebar、Header dropdown、その他モーダルで共通利用
 */
export const useModal = ({
  isOpen,
  onClose,
  containerRef,
  triggerRef,
  initialFocusRef,
  lockBodyScroll = false,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}: ModalOptions) => {
  // 統一キーボード管理フック
  const { registerHandler } = useGlobalKeyboard();
  // Body scroll lock/unlock
  useEffect(() => {
    if (!lockBodyScroll) return;

    if (isOpen) {
      // 現在のbodyスタイルを保存
      const originalPosition = document.body.style.position || '';
      const originalTop = document.body.style.top || '';
      const originalWidth = document.body.style.width || '';
      const originalOverflow = document.body.style.overflow || '';

      // 現在のスクロール位置を保存
      const scrollY = window.scrollY;

      // スクロールロック適用
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      // data属性に元の値を保存
      document.body.dataset.modalOriginalPosition = originalPosition;
      document.body.dataset.modalOriginalTop = originalTop;
      document.body.dataset.modalOriginalWidth = originalWidth;
      document.body.dataset.modalOriginalOverflow = originalOverflow;
      document.body.dataset.modalScrollY = scrollY.toString();
    } else {
      // 元のスタイルを復元
      const originalPosition = document.body.dataset.modalOriginalPosition || '';
      const originalTop = document.body.dataset.modalOriginalTop || '';
      const originalWidth = document.body.dataset.modalOriginalWidth || '';
      const originalOverflow = document.body.dataset.modalOriginalOverflow || '';
      const savedScrollY = document.body.dataset.modalScrollY || '0';

      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      document.body.style.overflow = originalOverflow;

      // data属性をクリーンアップ
      delete document.body.dataset.modalOriginalPosition;
      delete document.body.dataset.modalOriginalTop;
      delete document.body.dataset.modalOriginalWidth;
      delete document.body.dataset.modalOriginalOverflow;
      delete document.body.dataset.modalScrollY;

      // スクロール位置を復元
      window.scrollTo(0, parseInt(savedScrollY) || 0);
    }

    return () => {
      // クリーンアップ時は元の値を確実に復元
      const originalPosition = document.body.dataset.modalOriginalPosition || '';
      const originalTop = document.body.dataset.modalOriginalTop || '';
      const originalWidth = document.body.dataset.modalOriginalWidth || '';
      const originalOverflow = document.body.dataset.modalOriginalOverflow || '';

      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      document.body.style.overflow = originalOverflow;

      // data属性をクリーンアップ
      delete document.body.dataset.modalOriginalPosition;
      delete document.body.dataset.modalOriginalTop;
      delete document.body.dataset.modalOriginalWidth;
      delete document.body.dataset.modalOriginalOverflow;
      delete document.body.dataset.modalScrollY;
    };
  }, [isOpen, lockBodyScroll]);

  // Escape key handling（統一キーボード管理）
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return undefined;

    const unregister = registerHandler({
      id: `modal-escape-${Math.random().toString(36).substr(2, 9)}`,
      priority: KEYBOARD_PRIORITIES.MODAL,
      keys: ['Escape'],
      shouldIgnoreInputFocus: true, // 入力フィールドフォーカス時も動作
      handler: (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
          // フォーカスをトリガー要素に戻す
          if (triggerRef?.current) {
            triggerRef.current.focus();
          }
          return true; // 処理済み
        }
        return false; // 未処理
      },
    });

    return unregister;
  }, [isOpen, onClose, closeOnEscape, triggerRef, registerHandler]);

  // Tab trapping（統一キーボード管理）
  useEffect(() => {
    if (!isOpen || !containerRef?.current) return undefined;

    const unregister = registerHandler({
      id: `modal-tab-${Math.random().toString(36).substr(2, 9)}`,
      priority: KEYBOARD_PRIORITIES.MODAL,
      keys: ['Tab'],
      shouldIgnoreInputFocus: true, // 入力フィールドフォーカス時も動作
      handler: (e: KeyboardEvent) => {
        if (e.key !== 'Tab' || !containerRef?.current) return false;

        const focusableElements = containerRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (!firstElement || !lastElement) return false;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
          return true; // 処理済み
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
          return true; // 処理済み
        }

        return false; // 未処理
      },
    });

    return unregister;
  }, [isOpen, containerRef, registerHandler]);

  // Initial focus setting
  useEffect(() => {
    if (isOpen && initialFocusRef?.current) {
      const timer = setTimeout(() => {
        initialFocusRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isOpen, initialFocusRef]);

  // Outside click detection（共有メカニズム）
  useEffect(() => {
    if (!closeOnOverlayClick || !isOpen || !containerRef?.current) return undefined;

    const handleDocumentClick = (event: MouseEvent) => {
      // コンテナ外をクリックした場合にモーダルを閉じる
      if (!containerRef.current?.contains(event.target as Node)) {
        onClose();
        if (triggerRef?.current) {
          triggerRef.current.focus();
        }
      }
    };

    // mousedownイベントを使用（clickより早期に検出）
    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isOpen, closeOnOverlayClick, onClose, containerRef, triggerRef]);

  // Overlay click handler（従来の実装も保持）
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    }
  };

  return {
    handleOverlayClick,
  };
};
